import { GridCellParams, GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { toast } from 'react-hot-toast'

import { BaseTable } from 'Features/Projects/Table/BaseTable.view'

import { useUpdateProductAllocation } from './Allocations.data'
import {
  AllocationRowType,
  AllocationTableInterface,
  AllocationUpdateInterface,
} from './Allocations.types'

const keysToExclude = new Set(['baseSourceSiteCode', 'skuGroupCode', 'id'])
const onSuccess = () => {
  toast.success('Update Successful')
}
const onError = () => {
  toast.error('Update Failed')
}
const percentageValueFormatter = (value: GridCellParams) => {
  return value !== null && value !== undefined ? `${Number(value) * 100}%` : ''
}

export const AllocationsTable = ({
  data,
  isLoading,
  projectId,
  scenarioId,
  baskets,
}: AllocationTableInterface) => {
  const COLUMN_WIDTH = 100

  const { mutate: saveData, isLoading: isSaving } = useUpdateProductAllocation(onSuccess, onError)

  const updateTableData = (changedData: Record<string, AllocationRowType>) => {
    try {
      const updateArray: AllocationUpdateInterface[] = []

      // Validate changedData is not empty
      if (!changedData || Object.keys(changedData).length === 0) {
        toast.error('No changes detected')
        return
      }

      // Process changes
      for (const value of Object.values(changedData)) {
        // Ensure required fields exist
        if (!value.baseSourceSiteCode || !value.skuGroupCode) {
          toast.error('Missing required fields in data')
          return
        }

        for (const [key, val] of Object.entries(value)) {
          // Only include valid allocation entries
          if (!keysToExclude.has(key) && val !== null && val !== undefined) {
            updateArray.push({
              baseSourceSiteCode: value.baseSourceSiteCode,
              skuGroupCode: value.skuGroupCode,
              workcentreCode: key,
              workCentreAllocation: val,
            })
          }
        }
      }

      // Only proceed if there are actual updates
      if (updateArray.length > 0) {
        saveData({
          data: updateArray,
          projectId,
          scenarioId,
        })
      }
    } catch (error) {
      console.error('Error processing allocation updates:', error)
    }
  }

  const totalColumns = new Set(Object.keys(baskets).map((key) => `Total_${key}`))

  // Create column grouping model from baskets

  const [columns, columnGroupingModel]: [GridColDef[], GridColumnGroupingModel] = useMemo(() => {
    if (!data || data.length === 0) return [[], []]
    const fixedColumns: GridColDef[] = [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site Code',
        width: 150,
        type: 'string',
        flex: 1,
      },
      {
        field: 'skuGroupCode',
        headerName: 'SKU Group Code',
        width: 150,
        type: 'string',
        flex: 1,
      },
    ]

    const cgm = Object.entries(baskets).map(([siteCode, workcentres]) => {
      return {
        groupId: siteCode,
        headerName: `Site: ${siteCode}`,
        children: workcentres.map((workcentre) => ({
          field: workcentre,
          headerName: workcentre,
          flex: 1,
        })),
      }
    })

    cgm.forEach((group) => {
      group.children.forEach((child) => {
        fixedColumns.push({
          field: child.field,
          headerName: child.headerName,
          width: COLUMN_WIDTH,
          type: 'number',
          editable: true,
          valueFormatter: percentageValueFormatter,
          flex: 1,
        })
      })
    })

    return [[...fixedColumns], cgm]
  }, [data, baskets])

  // Prepare data with IDs if not already present
  const preparedData = useMemo(() => {
    return data.map((row, index) => ({
      ...row,
      id: row.id || `${row.baseSourceSiteCode}_${row.skuGroupCode}_${index}`,
    }))
  }, [data])

  const processPercentageUpdate = (row: AllocationRowType): AllocationRowType => {
    for (const [skuGroup, workcentres] of Object.entries(baskets)) {
      row[`Total_${skuGroup}`] = workcentres.reduce((total, workcentre) => {
        const value = `Total_${skuGroup}` === workcentre ? 0 : row[workcentre]
        return total + (value !== null && value !== undefined ? Number(value) : 0)
      }, 0)
    }
    return row
  }

  return (
    <BaseTable
      data={preparedData}
      columns={columns}
      isLoading={isLoading}
      updateTableData={updateTableData}
      uploadData={() => {
        // console.log('Upload data')
      }}
      isUploading={false}
      isSaving={isSaving}
      checkBoxSelection={true}
      pinnedColumns={['baseSourceSiteCode', 'skuGroupCode']}
      isCellEditable={(params: GridCellParams) => {
        const { field } = params
        return (
          params.row[field] !== null &&
          field !== 'baseSourceSiteCode' &&
          field !== 'skuGroupCode' &&
          !totalColumns.has(field)
        )
      }}
      columnGroupingModel={columnGroupingModel}
      processRow={processPercentageUpdate}
    />
  )
}
